import { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';
import { Icon } from './Icon.jsx';
import { cn } from '../lib/cn.js';

/**
 * Button — every labelled action.
 *
 * variant
 *   primary    the brand's action colour (MTN yellow, Aki Bua green). The ONE main action of a view.
 *   secondary  white, bordered. Everything else.
 *   brand      the brand's colour. Confirming a non-destructive decision inside a dialog.
 *   ghost      no chrome; toolbars and inline actions.
 *   danger     red text; the entry point to something destructive ("Reject…").
 *   danger-solid  red fill; the final confirm of something destructive.
 * size       sm 28px · md 32px · lg 36px
 * icon / iconRight   a lucide component
 * loading    shows a spinner in the icon slot, sets aria-busy and blocks clicks
 * disabled   looks disabled. Give `title` the reason; the button stays hoverable so it shows.
 *
 * `as` renders another element (a router <Link>, an <a>) with the same look.
 */
const BASE =
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded font-medium select-none transition-colors ' +
  'disabled:cursor-not-allowed aria-disabled:cursor-not-allowed';

const VARIANTS = {
  primary: 'bg-action text-on-action font-bold hover:bg-action-hover disabled:hover:bg-action',
  secondary: 'bg-white text-ink border border-line-strong hover:bg-surface-muted disabled:hover:bg-white',
  brand: 'bg-brand text-white font-bold hover:bg-brand-700 disabled:hover:bg-brand',
  ghost: 'bg-transparent text-ink hover:bg-surface-muted disabled:hover:bg-transparent',
  danger: 'bg-transparent text-failed-fg hover:bg-failed-bg disabled:hover:bg-transparent',
  'danger-solid': 'bg-danger-solid text-white font-bold hover:bg-danger-solid-hover disabled:hover:bg-danger-solid',
};

const SIZES = {
  sm: { box: 'h-7 px-2.5 text-sm', icon: 14 },
  md: { box: 'h-8 px-3 text-base', icon: 16 },
  lg: { box: 'h-9 px-3.5 text-base', icon: 16 },
};

export const Button = forwardRef(function Button(
  { as: Tag = 'button', variant = 'secondary', size = 'md', icon, iconRight, loading = false, disabled = false, fullWidth = false, className, children, type, onClick, ...rest },
  ref,
) {
  const s = SIZES[size];
  const isButton = Tag === 'button';
  const blocked = disabled || loading;
  return (
    <Tag
      ref={ref}
      type={isButton ? type || 'button' : undefined}
      disabled={isButton ? blocked : undefined}
      aria-disabled={!isButton && blocked ? true : undefined}
      aria-busy={loading || undefined}
      onClick={blocked ? (e) => e.preventDefault() : onClick}
      className={cn(BASE, VARIANTS[variant], s.box, blocked && 'opacity-50', fullWidth && 'w-full', className)}
      {...rest}
    >
      {loading ? <Icon icon={Loader2} size={s.icon} className="animate-spin" /> : icon ? <Icon icon={icon} size={s.icon} /> : null}
      {children != null ? <span className="truncate">{children}</span> : null}
      {iconRight ? <Icon icon={iconRight} size={s.icon} /> : null}
    </Tag>
  );
});

/**
 * IconButton — a square icon-only action (close, row "more", refresh). Always centred.
 * `label` is required: it is the accessible name and the hover title.
 * size  sm 28px (16px icon) · md 32px (18px) · lg 36px (20px)
 * tone  default · muted · danger · onDark (on the blue sidebar)
 */
const ICON_BUTTON_SIZES = { sm: ['h-7 w-7', 16], md: ['h-8 w-8', 18], lg: ['h-9 w-9', 20] };
const ICON_BUTTON_TONES = {
  default: 'text-neutral-fg hover:bg-surface-muted hover:text-ink',
  muted: 'text-ink-subtle hover:bg-divider hover:text-ink',
  danger: 'text-ink-subtle hover:bg-failed-bg hover:text-failed-fg',
  onDark: 'text-brand-300 hover:bg-nav-hover hover:text-white',
  // Beside secondary buttons (a header's "⋯"): the same border and fill, so the row reads as one set.
  outline: 'border border-line-strong bg-white text-ink hover:bg-surface-muted',
};

export const IconButton = forwardRef(function IconButton(
  { icon, label, size = 'md', tone = 'default', loading = false, disabled = false, active = false, className, type = 'button', badge, ...rest },
  ref,
) {
  const [box, px] = ICON_BUTTON_SIZES[size];
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={rest.title ?? label}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center rounded transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        box,
        ICON_BUTTON_TONES[tone],
        active && 'bg-surface-muted text-ink',
        className,
      )}
      {...rest}
    >
      <Icon icon={loading ? Loader2 : icon} size={px} className={loading ? 'animate-spin' : undefined} />
      {badge ? <span className="absolute right-1 top-1 h-2 w-2 rounded-full border-2 border-white bg-failed-dot" aria-hidden="true" /> : null}
    </button>
  );
});
